import { memo, useMemo, type ComponentType } from "react";
import {
  ArrowLeft,
  BadgeCheck,
  Fuel,
  Gauge,
  Gavel,
  GitCompare,
  Heart,
  MapPin,
  Settings2,
  Zap,
} from "lucide-react";
import { DealershipListingIcon } from "@/components/car-listing/dealership-listing-icon";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { ListingCarCardData } from "@/components/car-listing/listing-car-types";
import { formatListingCardPrice } from "@/components/car-listing/listing-car-types";
import { formatSiteNumber } from "@shared/currency";
import { ListingCardGallery } from "@/components/car-listing/listing-card-gallery";
import { useCarCompareOptional } from "@/components/car-listing/car-compare-context";
import { dealershipPublicHref, isPublicDealershipSeller } from "@/lib/dealership-public";
import { AuctionConditionChip } from "@/components/auction-condition/auction-condition-chip";

export function CustomsExemptLabel({ show }: { show?: boolean }) {
  if (!show) return null;
  return (
    <span className="pointer-events-none absolute bottom-5 start-5 z-10 rounded-md bg-[#0071BC] px-2.5 py-1 text-xs font-bold text-white shadow-sm">
      اعفاء جمركي
    </span>
  );
}

export function DriveChip({ car }: { car: ListingCarCardData }) {
  const fuel = car.fuelType || "";

  if (fuel.includes("كهرب") || fuel.toLowerCase().includes("electric")) {
    return (
      <Badge
        variant="outline"
        className="h-5 border-emerald-200 bg-emerald-50 px-1.5 text-[10px] font-medium text-emerald-800"
      >
        <Zap className="me-0.5 size-2.5" />
        كهربائي
      </Badge>
    );
  }

  if (fuel.includes("قابل للشحن")) {
    return (
      <Badge
        variant="outline"
        className="h-auto min-h-5 max-w-full whitespace-normal border-violet-200 bg-violet-50 px-1.5 py-0.5 text-start text-[10px] font-medium leading-tight text-violet-800 overflow-visible"
      >
        <Fuel className="me-0.5 size-2.5" />
        {fuel}
      </Badge>
    );
  }

  if (fuel === "هجين") {
    return (
      <Badge
        variant="outline"
        className="h-5 border-blue-200 bg-blue-50 px-1.5 text-[10px] font-medium text-blue-800"
      >
        <Zap className="me-0.5 size-2.5" />
        هجين
      </Badge>
    );
  }

  if (car.transmission) {
    return (
      <Badge variant="outline" className="h-auto min-h-5 max-w-full whitespace-normal px-1.5 py-0.5 text-start text-[10px] font-medium leading-tight text-muted-foreground overflow-visible">
        {car.transmission}
      </Badge>
    );
  }

  return (
    <Badge variant="outline" className="h-auto min-h-5 max-w-full whitespace-normal px-1.5 py-0.5 text-start text-[10px] font-medium leading-tight text-muted-foreground overflow-visible">
      {car.fuelType || "·"}
    </Badge>
  );
}

type CardFeature = {
  icon: ComponentType<{ className?: string }>;
  label: string;
};

function buildCardFeatures(car: ListingCarCardData): CardFeature[] {
  const features: CardFeature[] = [];

  if (car.saleType === "auction") {
    features.push({ icon: Gavel, label: "مزاد حي" });
  } else {
    features.push({ icon: BadgeCheck, label: "بيع مباشر" });
  }

  if (car.transmission?.trim()) {
    features.push({ icon: Settings2, label: car.transmission.trim() });
  }

  if (car.fuelType?.trim()) {
    features.push({ icon: Fuel, label: car.fuelType.trim() });
  }

  if (car.mileage > 0) {
    features.push({ icon: Gauge, label: `${formatSiteNumber(car.mileage)} كم` });
  }

  if (car.location?.trim()) {
    features.push({ icon: MapPin, label: car.location.trim() });
  }

  return features.slice(0, 5);
}

export function FeatureItem({ icon: Icon, label }: CardFeature) {
  return (
    <div className="flex min-w-0 items-start gap-1.5 text-xs text-neutral-600">
      <Icon className="mt-0.5 size-3.5 shrink-0 text-neutral-400" />
      <span className="min-w-0 text-start leading-snug break-words">{label}</span>
    </div>
  );
}

export type ListingCarCardProps = {
  car: ListingCarCardData;
  isFavorite: boolean;
  onToggleFavorite: (carId: number) => void;
  variant?: "default" | "fixed";
  className?: string;
  hideFavoriteButton?: boolean;
  /** Show compare toggle (requires CarCompareProvider on listings page). */
  enableCompare?: boolean;
  /** صفحة المزادات · لون تحميل وعنوان متوافق مع الهوية */
  brandAccent?: boolean;
  /** Eager-load main image (LCP candidate on first grid card). */
  imagePriority?: boolean;
};

export const ListingCarCard = memo(function ListingCarCard({
  car,
  isFavorite,
  onToggleFavorite,
  variant = "default",
  className,
  hideFavoriteButton = false,
  enableCompare = false,
  imagePriority,
}: ListingCarCardProps) {
  const compare = useCarCompareOptional();
  const compareEnabled = enableCompare && compare != null;
  const isCompareSelected = compareEnabled && compare.isSelected(car.id);
  const compareSlotsFull =
    compareEnabled && !isCompareSelected && compare.count >= 2;

  const priceText = formatListingCardPrice(car);
  const detailHref = `/car/${car.id}?sale=${car.saleType}`;
  const features = useMemo(() => buildCardFeatures(car), [car]);
  return (
    <article
      className={cn(
        "group flex h-full flex-col overflow-hidden rounded-2xl border border-neutral-100 bg-white shadow-sm transition-all duration-300",
        "hover:-translate-y-0.5 hover:shadow-lg",
        variant === "fixed" && "border-neutral-100",
        className
      )}
    >
      {/* Image */}
      <div className="relative p-3 pb-0">
        <Link
          href={detailHref}
          className="block outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <ListingCardGallery
            mainImage={car.mainImage}
            galleryPreview={car.galleryPreview}
            alt={car.titleAr}
            className="aspect-[4/3] w-full rounded-2xl"
            priorityMain={imagePriority}
          />
        </Link>

        {compareEnabled ? (
          <button
            type="button"
            disabled={compareSlotsFull}
            className={cn(
              "absolute bottom-4 end-4 z-10 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold shadow-md transition-colors",
              isCompareSelected
                ? "bg-[#0071BC] text-white hover:bg-[#005a94]"
                : compareSlotsFull
                  ? "cursor-not-allowed bg-neutral-200/95 text-neutral-400"
                  : "bg-white/95 text-neutral-800 hover:bg-white",
            )}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (compareSlotsFull) return;
              compare.toggleCar(car);
            }}
            aria-pressed={isCompareSelected}
            aria-disabled={compareSlotsFull}
            aria-label={
              isCompareSelected
                ? "إزالة من المقارنة"
                : compareSlotsFull
                  ? "المقارنة ممتلئة (سيارتان)"
                  : "إضافة للمقارنة"
            }
          >
            <GitCompare className="size-3.5 shrink-0" aria-hidden />
            مقارنة
          </button>
        ) : null}

        <CustomsExemptLabel show={car.customsExempt} />

        <div className="absolute start-5 top-5 z-10 flex flex-col gap-1.5">
          <span className="inline-flex items-center rounded-full bg-white/95 px-2.5 py-1 text-xs font-semibold text-neutral-800 shadow-sm backdrop-blur-sm">
            {car.year}
          </span>
        </div>

        {!hideFavoriteButton ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute end-5 top-5 z-30 size-9 rounded-full border-0 bg-white/95 text-neutral-500 shadow-sm backdrop-blur-sm hover:bg-white hover:text-neutral-900 pointer-events-auto"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleFavorite(car.id);
            }}
            aria-label={isFavorite ? "إزالة من المفضلة" : "إضافة للمفضلة"}
          >
            <Heart
              className={cn("size-4", isFavorite ? "fill-red-500 text-red-500" : "text-neutral-500")}
            />
          </Button>
        ) : null}
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col px-4 pb-4 pt-3">
        <h3 className="line-clamp-2 text-lg font-bold leading-snug text-neutral-900 sm:text-xl">
          <Link href={detailHref} className="transition-colors hover:text-[#0071BC]">
            {car.titleAr}
          </Link>
        </h3>

        <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 sm:grid-cols-3">
          {features.map((feature) => (
            <FeatureItem key={feature.label} {...feature} />
          ))}
        </div>

        {isPublicDealershipSeller(car) ? (
          <p className="mt-3 flex items-center gap-2.5 text-sm font-medium text-muted-foreground">
            <DealershipListingIcon size="md" />
            <Link
              href={dealershipPublicHref(car.sellerId)}
              className="truncate font-medium text-(--logo-blue) hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {car.dealershipName?.trim() || car.sellerDisplayName?.trim() || "المعرض"}
            </Link>
          </p>
        ) : null}

        {car.saleType === "auction" ? (
          <div className="mt-3">
            <AuctionConditionChip gradeId={car.auctionCondition} />
          </div>
        ) : null}

        {/* Footer: CTA + price (swapped) */}
        <div className="mt-auto flex items-end justify-between gap-3 pt-4">
          <Button
            asChild
            size="sm"
            className="h-10 shrink-0 rounded-full bg-neutral-900 px-5 text-sm font-semibold text-white shadow-sm hover:bg-neutral-800"
          >
            <Link href={detailHref}>
              عرض التفاصيل
              <ArrowLeft className="size-4" />
            </Link>
          </Button>

          <div className="min-w-0 text-end">
            <p className="text-2xl font-bold leading-none tracking-tight text-accent">
              {priceText ?? "·"}
            </p>
            {car.saleType === "auction" ? (
              <p className="mt-1 text-xs text-neutral-400">/مزاد</p>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
});

ListingCarCard.displayName = "ListingCarCard";
